export const invoicePrompt = {
  moduleName: "Customer Invoice & Vendor Bill",
  purpose: "Create and manage customer invoices and vendor bills with line items, GL journal integration, payment application, and multi-currency support.",
  techStack: "React, Supabase, Double-Entry Accounting",
  version: "v1.3.0",
  rules: [
    "Invoice number auto-generated sequentially (INVYYxxxxx)",
    "Bill number auto-generated sequentially (BILL-XXXXXX)",
    "Line items require charge code with mandatory AR (Revenue) and AP (Cost) GL account mappings",
    "Multi-currency with ROE (rate of exchange) per line item",
    "Metadata JSONB column stores supplemental fields (division, vessel, weight, etc.)",
    "Payment toggles: Wire, Zelle, Credit Card display preferences in metadata",
    "Delete only allowed for draft status; Void available for posted invoices with paid/posted guards",
    "GL Journal entries support double-entry with balanced debit/credit validation",
    "Payment application auto-updates balance_due and status (paid/partially_paid)",
  ],
  logic: [
    "Line Total = Quantity × Unit Price",
    "Tax Amount = Line Total × Tax Rate",
    "Invoice Total = sum of (Line Total + Tax Amount) for all lines",
    "Balance Due = Invoice Total - Paid Amount",
    "Payment Application: applied_amount reduces balance_due; auto-sets status when fully paid",
    "GL Journal: Total Debit must equal Total Credit for posting",
    "Journal Reversal: creates counter-entry with swapped debit/credit amounts",
  ],
  processFlow: [
    "User creates invoice/bill from Invoice list or Job Costing flow",
    "Selects customer/vendor from address book",
    "Adds line items with charge code, quantity, unit price, tax",
    "Links to booking/shipment for context",
    "Fills metadata fields (division, vessel, terms, payment toggles)",
    "Saves invoice → auto-generates invoice number",
    "Posts invoice → creates GL journal entry",
    "Records payment → applies to invoice/bill → updates balance",
    "Void/Delete with accounting guards",
  ],
  relatedDocuments: [
    { module: "Booking", relationship: "Invoice/bill linked to booking for shipment context" },
    { module: "Job Costing", relationship: "AR/AP charges from booking drive invoice/bill creation" },
    { module: "Chart of Accounts", relationship: "GL accounts for charge code mapping" },
    { module: "GL Journals", relationship: "Invoice posting creates GL journal entries" },
    { module: "Payments", relationship: "Payment application reduces invoice/bill balance" },
    { module: "Address Book", relationship: "Customer/vendor selection from mst_parties" },
  ],
  databaseTables: [
    { name: "acc_customer_invoices", purpose: "Invoice header with customer, dates, totals, metadata JSONB", keyColumns: ["id", "invoice_no", "customer_id", "booking_id", "status", "total_amount", "balance_due", "metadata"] },
    { name: "acc_invoice_lines", purpose: "Invoice line items with charge code, GL account, amounts", keyColumns: ["id", "invoice_id", "charge_code_id", "gl_account_id", "quantity", "unit_price", "amount", "total_amount"] },
    { name: "acc_vendor_bills", purpose: "Vendor bill header", keyColumns: ["id", "bill_no", "vendor_id", "booking_id", "status", "total_amount", "balance_due"] },
    { name: "acc_bill_lines", purpose: "Bill line items", keyColumns: ["id", "bill_id", "charge_code_id", "gl_account_id", "quantity", "unit_price", "amount"] },
    { name: "acc_payments", purpose: "Payment records (receipts and vendor payments)", keyColumns: ["id", "payment_no", "payment_type", "amount", "party_id", "status"] },
    { name: "acc_payment_applications", purpose: "Links payments to invoices/bills", keyColumns: ["id", "payment_id", "invoice_id", "bill_id", "applied_amount"] },
    { name: "acc_gl_journals", purpose: "GL journal entries (double-entry)", keyColumns: ["id", "journal_no", "journal_type", "journal_date", "total_debit", "total_credit", "is_posted"] },
    { name: "acc_gl_journal_lines", purpose: "Journal line items with debit/credit", keyColumns: ["id", "journal_id", "account_id", "debit_amount", "credit_amount"] },
    { name: "mst_charge_codes", purpose: "Charge code definitions with GL mappings", keyColumns: ["id", "charge_code", "description", "ar_gl_account_id", "ap_gl_account_id"] },
  ],
  edgeFunctions: [],
  aiFeatures: [],
  endToEndFlow: [
    "Booking created with AR/AP charges",
    "Job Costing page shows P&L with charge status",
    "User selects charges → creates Invoice (AR) or Bill (AP)",
    "Invoice/bill persisted with charge code GL mapping",
    "Invoice posted → GL journal entry created (double-entry)",
    "Customer pays → payment recorded and applied to invoice",
    "Balance updated → invoice marked as paid",
    "Reporting: aging, revenue, P&L from GL data",
  ],
};